"use client";

import {
	RiCloseLine,
	RiHeartFill,
	RiStarFill,
	RiStarHalfFill,
	RiStarLine,
} from "@remixicon/react";
import { cn } from "@workspace/ui/lib/utils";
import {
	type MouseEvent,
	memo,
	type TouchEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

export interface StarRatingProps {
	/** Rating value on a 0-10 scale */
	rating: number;
	/** Callback when rating changes (only for interactive mode) */
	onChange?: (newRating: number) => void;
	/** Whether the component is interactive or readonly */
	readOnly?: boolean;
	/** Size variant */
	size?: "sm" | "md" | "lg";
	/** Additional CSS class name */
	className?: string;
	/** Whether to show the clear button (interactive mode only) */
	showClearButton?: boolean;
	/** Whether the user loved this (shows heart icon) */
	loved?: boolean;
	/** Whether to show partial review indicator (outline stars for reviews without overall rating) */
	hasPartialReview?: boolean;
	/** Whether to enable advanced confetti animations on click */
	enableConfettiAnimations?: boolean;
}

/**
 * Comprehensive StarRating component that consolidates all star rating implementations.
 * Supports both readonly display and interactive rating input with proper accessibility.
 *
 * Features:
 * - Readonly and interactive modes
 * - Multiple sizes (sm, md, lg)
 * - Half-star precision for display
 * - Touch and mouse support for interaction
 * - Clear button functionality
 * - Accessibility support
 * - Performance optimized with memo and memoized calculations
 */
export const StarRating = memo<StarRatingProps>(
	({
		rating,
		onChange,
		readOnly = true,
		size = "md",
		className,
		showClearButton = true,
		loved = false,
		hasPartialReview = false,
		enableConfettiAnimations = false,
	}) => {
		// Interactive state
		const [hoverRating, setHoverRating] = useState<number>(0);
		const [activeStarIndex, setActiveStarIndex] = useState<number | null>(null);
		const [animatedStars, setAnimatedStars] = useState<Set<number>>(new Set());
		const [isTouchDevice, setIsTouchDevice] = useState(false);
		const starsRef = useRef<HTMLDivElement>(null);

		// Detect touch device on mount
		useEffect(() => {
			setIsTouchDevice(
				"ontouchstart" in window || navigator.maxTouchPoints > 0,
			);
		}, []);

		// Size configurations
		const sizeConfig = useMemo(() => {
			switch (size) {
				case "sm":
					return {
						starSize: "w-3 h-3",
						containerHeight: readOnly ? "h-auto" : "h-8",
						containerWidth: readOnly ? "w-auto" : "w-8",
						clearButtonSize: "w-5 h-5",
						clearButtonClasses: "w-5 h-5",
					};
				case "lg":
					return {
						starSize: "w-5 h-5",
						containerHeight: readOnly ? "h-auto" : "h-12",
						containerWidth: readOnly ? "w-auto" : "w-12",
						clearButtonSize: "w-7 h-7",
						clearButtonClasses: "w-6 h-6",
					};
				default:
					return {
						starSize: "w-4 h-4",
						containerHeight: readOnly ? "h-auto" : "h-11",
						containerWidth: readOnly ? "w-auto" : "w-11",
						clearButtonSize: "w-6 h-6",
						clearButtonClasses: "w-5 h-5",
					};
			}
		}, [size, readOnly]);

		// Convert rating to star display values - memoized for performance
		const starData = useMemo(() => {
			const numericRating =
				rating !== null && rating !== undefined ? Number(rating) : 0;
			const isValidRating = !Number.isNaN(numericRating) && numericRating > 0;

			// For partial reviews without overall rating, show outline stars as visual indicator
			const showPartialReviewIndicator = hasPartialReview && !isValidRating;

			// Convert from 0-10 scale to 0-5 scale for star display
			const starRating = numericRating / 2;
			const wholeStars = Math.floor(starRating);
			// Standardized half-star detection (ratings are integers 0-10)
			const hasHalfStar = starRating % 1 >= 0.5;

			return {
				numericRating,
				isValidRating,
				showPartialReviewIndicator,
				starRating,
				wholeStars,
				hasHalfStar,
			};
		}, [rating, hasPartialReview]);

		// Calculate rating based on star position (for interactive mode)
		const calculateRating = useCallback(
			(index: number, position: number): number => {
				if (position <= 0.5) {
					return index * 2 + 1; // Left half of star
				}
				return index * 2 + 2; // Right half of star
			},
			[],
		);

		// Container-level mouse move handler (performance optimized)
		const handleContainerMouseMove = useCallback(
			(e: MouseEvent) => {
				if (readOnly || !onChange || isTouchDevice) return;

				const container = e.currentTarget;
				const rect = container.getBoundingClientRect();
				const x = e.clientX - rect.left;

				// Find which star we're hovering over
				const starElements = container.querySelectorAll("[data-star-index]");
				let targetStarIndex = -1;
				let relativePosition = 0;

				for (let i = 0; i < starElements.length; i++) {
					const starElement = starElements[i] as HTMLElement;
					const starRect = starElement.getBoundingClientRect();
					const starLeft = starRect.left - rect.left;
					const starRight = starRect.right - rect.left;

					if (x >= starLeft && x <= starRight) {
						targetStarIndex = i;
						relativePosition = (x - starLeft) / starRect.width;
						break;
					}
				}

				if (targetStarIndex >= 0) {
					const starValue = calculateRating(targetStarIndex, relativePosition);
					setHoverRating(Math.max(1, Math.min(10, Math.round(starValue))));
				}
			},
			[readOnly, onChange, isTouchDevice, calculateRating],
		);

		// Container-level mouse leave handler (ensures hover is always cleared)
		const handleContainerMouseLeave = useCallback(() => {
			if (!isTouchDevice) {
				setHoverRating(0);
			}
		}, [isTouchDevice]);

		const handleStarClick = useCallback(
			(index: number, clickedRating: number) => {
				if (readOnly || !onChange) return;

				const newRating = rating === clickedRating ? 0 : clickedRating;
				onChange(newRating);

				// Visual feedback on click
				setHoverRating(0);
				setActiveStarIndex(index);

				// Enhanced visual feedback with confetti animations (if enabled)
				if (enableConfettiAnimations && newRating > 0) {
					const starsToAnimate = new Set<number>();
					for (let i = 0; i <= Math.floor((newRating - 1) / 2); i++) {
						starsToAnimate.add(i);
					}
					setAnimatedStars(starsToAnimate);

					// Clear animations after delay
					setTimeout(() => {
						setAnimatedStars(new Set());
					}, 600);
				}

				setTimeout(() => {
					setActiveStarIndex(null);
				}, 200);
			},
			[rating, onChange, readOnly, enableConfettiAnimations],
		);

		const handleClearRating = useCallback(
			(e: MouseEvent | TouchEvent) => {
				if (readOnly || !onChange) return;

				e.stopPropagation();
				onChange(0);
				setHoverRating(0);
			},
			[onChange, readOnly],
		);

		// Touch handling for mobile devices
		const handleTouchMove = useCallback(
			(e: TouchEvent) => {
				if (readOnly || !onChange) return;

				e.preventDefault();
				const touch = e.touches[0];
				if (!touch || !starsRef.current) return;

				const elements = document.elementsFromPoint(
					touch.clientX,
					touch.clientY,
				);
				const starElement = elements.find((el) =>
					el.hasAttribute("data-star-index"),
				);

				if (starElement) {
					const starIndex = Number.parseInt(
						starElement.getAttribute("data-star-index") || "0",
						10,
					);
					const rect = starElement.getBoundingClientRect();
					const position = (touch.clientX - rect.left) / rect.width;
					const starValue = calculateRating(starIndex, position);
					setHoverRating(Math.max(1, Math.min(10, Math.round(starValue))));
				}
			},
			[readOnly, onChange, calculateRating],
		);

		// Render star icon based on rating and hover state
		const renderStar = useCallback(
			(index: number) => {
				const starPosition = index + 1;
				const currentDisplayRating =
					!readOnly && hoverRating > 0 ? hoverRating : rating;

				// Determine star type and styling
				let StarIcon = RiStarLine;
				let iconClass = "text-muted-foreground/50";

				if (starData.showPartialReviewIndicator) {
					// Show outline stars for partial reviews
					StarIcon = RiStarLine;
					iconClass = "text-muted-foreground";
				} else if (!starData.isValidRating) {
					// No rating - empty stars
					StarIcon = RiStarLine;
					iconClass = "text-muted-foreground/50";
				} else {
					// Display logic for filled stars
					if ((index + 1) * 2 <= currentDisplayRating) {
						// Full star
						StarIcon = RiStarFill;
						iconClass =
							!readOnly && hoverRating > 0
								? "text-yellow-300"
								: "text-yellow-500";
					} else if ((index + 1) * 2 - 1 === currentDisplayRating) {
						// Half star
						StarIcon = RiStarHalfFill;
						iconClass =
							!readOnly && hoverRating > 0
								? "text-yellow-300"
								: "text-yellow-500";
					} else if (starPosition <= starData.wholeStars) {
						// Full star (for readonly with calculated half stars)
						StarIcon = RiStarFill;
						iconClass = "text-yellow-500";
					} else if (
						starPosition === starData.wholeStars + 1 &&
						starData.hasHalfStar
					) {
						// Half star implementation for readonly
						return (
							<div
								key={index}
								className={cn(
									"relative",
									enableConfettiAnimations &&
										animatedStars.has(index) &&
										"",
								)}
							>
								<RiStarLine
									className={cn(
										sizeConfig.starSize,
										"text-muted-foreground/50",
									)}
								/>
								<div className="absolute top-0 left-0 w-[50%] overflow-hidden">
									<RiStarFill
										className={cn(sizeConfig.starSize, "text-yellow-500")}
									/>
								</div>
							</div>
						);
					}
				}

				return (
					<div
						key={index}
						className={cn(
							enableConfettiAnimations &&
								animatedStars.has(index) &&
								"",
						)}
					>
						<StarIcon
							className={cn(
								sizeConfig.starSize,
								iconClass,
								"transition-all duration-150",
								!readOnly && activeStarIndex === index && "animate-pulse",
							)}
						/>
					</div>
				);
			},
			[
				rating,
				hoverRating,
				readOnly,
				activeStarIndex,
				starData,
				sizeConfig.starSize,
				enableConfettiAnimations,
				animatedStars,
			],
		);

		// Readonly mode - simple star display
		if (readOnly) {
			return (
				<div className={cn("flex items-center", className)}>
					<div className="flex items-center">
						{[0, 1, 2, 3, 4].map(renderStar)}
					</div>

					{/* Heart if loved - positioned right after stars */}
					{loved && (
						<div className="ml-2">
							<RiHeartFill
								className={cn(sizeConfig.starSize, "text-red-500")}
							/>
						</div>
					)}
				</div>
			);
		}

		// Interactive mode - full interaction support
		return (
			<div
				className={cn("group relative flex touch-none items-center", className)}
				role="radiogroup"
				aria-label="Rating out of 10"
				onTouchStart={(e) => e.stopPropagation()}
			>
				{/* Clear button for interactive mode */}
				{rating > 0 && showClearButton && (
					<div
						className={cn(
							"-left-7 md:-left-8 -translate-y-1/2 absolute top-1/2 z-10",
							"cursor-pointer text-muted-foreground transition-colors duration-200 hover:text-foreground",
							"hidden md:block", // Hide on mobile, show on md and larger
							isTouchDevice
								? "flex h-8 w-8 items-center justify-center opacity-100"
								: "w-6 opacity-0 group-hover:opacity-100",
						)}
						onClick={handleClearRating}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleClearRating(e);
						}}
						aria-label="Clear rating"
					>
						<RiCloseLine
							className={cn(
								isTouchDevice
									? sizeConfig.clearButtonSize
									: sizeConfig.clearButtonClasses,
							)}
						/>
					</div>
				)}

				{/* Stars container */}
				<div
					className="relative flex gap-1"
					ref={starsRef}
					onMouseMove={handleContainerMouseMove}
					onMouseLeave={handleContainerMouseLeave}
					onTouchMove={handleTouchMove}
					onTouchStart={(e) => e.stopPropagation()}
					onTouchEnd={(e) => {
						e.preventDefault();
						e.stopPropagation();
						if (hoverRating > 0) {
							handleStarClick(activeStarIndex || 0, hoverRating);
						}
						setHoverRating(0);
					}}
				>
					{[0, 1, 2, 3, 4].map((index) => (
						<div
							key={index}
							className={cn(
								"relative cursor-pointer touch-manipulation",
								sizeConfig.containerHeight,
								sizeConfig.containerWidth,
								"flex items-center justify-center",
							)}
							data-star-index={index}
							onTouchStart={(e) => {
								e.preventDefault();
								e.stopPropagation();
								const rect = e.currentTarget.getBoundingClientRect();
								const touch = e.touches[0];
								if (!touch) return;
								const x = touch.clientX - rect.left;
								const position = x / rect.width;
								const starValue = calculateRating(index, position);
								setHoverRating(
									Math.max(1, Math.min(10, Math.round(starValue))),
								);
							}}
							onClick={() => {
								if (hoverRating > 0) {
									handleStarClick(index, hoverRating);
								}
							}}
							aria-label={`${index + 1} stars`}
							role="radio"
							aria-checked={Math.ceil(rating / 2) === index + 1}
						>
							{renderStar(index)}
						</div>
					))}
				</div>

				{/* Screen reader text */}
				<span className="sr-only">{`Rating: ${rating} out of 10`}</span>
			</div>
		);
	},
);

StarRating.displayName = "StarRating";
